/**
 * BotSilenceStatus (KAN-686): who answers the client right now.
 *
 * * manual mode: "Бот молчит, вы пишете сами";
 * * handed to a manager (bot_silence.reason == "handed_to_human"):
 *   "Передан менеджеру: бот отвечает, сам не пишет" (KAN-820: replies go on,
 *   only the bot's own follow-ups stop) and the "Вернуть боту" button;
 * * otherwise: "Отвечает бот".
 *
 * Used next to the manual mode switch in the transcript modal and in the
 * status block of the client page.
 */
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { BotSilence } from '@/types/client';
import { useReturnToBot } from './useReturnToBot';

const BOT_ANSWERS_TEXT = 'Отвечает бот';
const MANUAL_MODE_TEXT = 'Бот молчит, вы пишете сами';
const HANDED_TO_HUMAN_TEXT = 'Передан менеджеру: бот отвечает, сам не пишет';

interface BotSilenceStatusProps {
  clientId: string;
  manualMode: boolean;
  botSilence?: BotSilence | null;
  /** data-testid of the caption. */
  testId?: string;
}

export function BotSilenceStatus({
  clientId,
  manualMode,
  botSilence,
  testId = 'bot-silence-hint',
}: BotSilenceStatusProps): JSX.Element {
  const returnToBot = useReturnToBot(clientId);
  const handed = !manualMode && botSilence?.reason === 'handed_to_human';
  const text = manualMode
    ? MANUAL_MODE_TEXT
    : handed
      ? HANDED_TO_HUMAN_TEXT
      : BOT_ANSWERS_TEXT;

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span
        className={handed ? 'text-amber-700' : 'text-gray-500'}
        data-testid={testId}
      >
        {text}
      </span>
      {handed && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={returnToBot.isPending}
          onClick={() => returnToBot.mutate()}
          data-testid="return-to-bot-button"
        >
          {returnToBot.isPending && (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          )}
          Вернуть боту
        </Button>
      )}
    </span>
  );
}
